import styled from '@emotion/styled'
import { LazyLoadingRule } from '@pubstack/common/src/adm/rules'
import { type AdUnit, getAdUnitMediatypes } from '@pubstack/common/src/adunit'
import { CurrencySymbol } from '@pubstack/common/src/currency'
import { BIDDER_RULE_SECTION_LABELS, BIDDER_RULE_SECTIONS } from '@pubstack/common/src/bidder-rule'
import type { DisplayedBidder, DisplayedFloorRule, DisplayedHeaderBiddingRule } from '@pubstack/common/src/stack'
import { MediaTypeBadge } from '~/components/MediaTypeBadge'
import type { FunctionComponent, ReactNode } from 'react'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import Chip from '~/components/Chip'
import { Icon } from '~/components/Icon'
import { Qualifier } from '~/components/Qualifier'
import { useProduct } from '~/hooks/useProduct'
import type { WithClassName } from '~/types/utils'
import { displayWithCurrency } from '~/utils/string'

const RichDetailsWrapper = styled.div`
  overflow: hidden;
  background: ${Colors.White};
  ${Fonts.colors.Jet}
  margin: -10px;
  border-radius: 4px;
  padding: 10px;
  max-width: 480px;
  box-shadow: 0 3px 10px rgba(54, 54, 54, 0.3);
  ${Fonts.P3}
  h3 {
    ${Fonts.P3}
    color: ${Colors.Jet};
    font-weight: 700;
  }
  h2 {
    ${Fonts.P3}
    color: ${Colors.Warning};
    font-weight: normal;
    display: flex;
    align-items: center;
    gap: 4px;
  }
  
  strong {
    font-weight: 500;
  }

  em {
    font-style: italic;
  }
`
const CenterItems = styled.div`
  div {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`
const Qualifiers = styled.div`
  padding-top: 6px;
`
const Divider = styled.div`
  margin: 8px 0;
  height: 1px;
  max-height: 1px;
  min-height: 1px;
  flex: 1;
  background-color: ${Colors.Platinum};
`

const Archived = styled.div`
  display: flex;
`
const FloorWrapper = styled.span`
  ${Fonts.P2}
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const AllSizesChip = styled(Chip)`
  width: fit-content;
  color: ${Colors.SlateGrey};
  font-style: italic;
`
const TargetValues = styled.span`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${Chip} {
    width: fit-content;
    flex-direction: row;
  }
  div {
    display: flex;
    flex-direction: row;
  }
`

const IconBackground = styled.div`
  background-color: ${() => Colors.Success};
  width: 34px;
  height: 34px;
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
`

type RichDetailsProps = WithClassName & {
  name?: string
  adUnit?: AdUnit
  bidder?: DisplayedBidder
  conditions?: ReactNode
  differences?: string[]
  lazyLoadingRule?: LazyLoadingRule
  isDisabled?: boolean
  floorRule?: DisplayedFloorRule
  bidderRule?: DisplayedHeaderBiddingRule
  currencySymbol?: CurrencySymbol
}

const BaseRichDetails: FunctionComponent<RichDetailsProps> = ({ name, adUnit, bidder, conditions, differences, lazyLoadingRule, isDisabled, floorRule, bidderRule, currencySymbol }) => {
  const { product } = useProduct()
  return (
    <RichDetailsWrapper>
      <CenterItems>
        {name && !adUnit && !bidder && !floorRule && !bidderRule && <span>{name}</span>}
        {adUnit && (
          <>
            <div>
              <h3>name in ad server:</h3> {adUnit.adServerAdUnitName}
            </div>
            <div>
              <h3>div ID:</h3> {adUnit.divId}
            </div>
            {getAdUnitMediatypes(adUnit).includes('Instream') ? (
              <Qualifiers>
                <Qualifier
                  iconName={'header_bidding'}
                  tooltipText={adUnit.headerBiddingEnabled ? 'Header bidding enabled' : 'Header bidding disabled'}
                  color={Colors.Success}
                  active={adUnit.headerBiddingEnabled && !isDisabled}
                  enabled={adUnit.enabled}
                />
                <Qualifier iconName={'outstream'} tooltipText={`Instream enabled`} active={true} enabled={true} />
              </Qualifiers>
            ) : (
              <Qualifiers>
                <Qualifier
                  iconName={'header_bidding'}
                  tooltipText={adUnit.headerBiddingEnabled ? 'Header bidding enabled' : 'Header bidding disabled'}
                  color={Colors.Success}
                  active={adUnit.headerBiddingEnabled && !isDisabled}
                  enabled={adUnit.enabled}
                />
                <Qualifier
                  iconName={'dynamic'}
                  tooltipText={adUnit.dynamicEnabled ? 'Dynamic enabled' : 'Dynamic disabled'}
                  color={Colors.Success}
                  active={adUnit.dynamicEnabled && !isDisabled}
                  enabled={adUnit.enabled}
                />
                <Qualifier
                  iconName={'banner'}
                  tooltipText={getAdUnitMediatypes(adUnit).includes('Banner') ? 'Banner enabled' : 'Banner disabled'}
                  color={Colors.Success}
                  active={getAdUnitMediatypes(adUnit).includes('Banner') && !isDisabled}
                  enabled={adUnit.enabled}
                />
                <Qualifier
                  iconName={'outstream2'}
                  tooltipText={getAdUnitMediatypes(adUnit).includes('Outstream') ? 'Outstream enabled' : 'Outstream disabled'}
                  color={Colors.Success}
                  active={getAdUnitMediatypes(adUnit).includes('Outstream') && !isDisabled}
                  enabled={adUnit.enabled}
                />
                <Qualifier
                  iconName={'native'}
                  tooltipText={getAdUnitMediatypes(adUnit).includes('Native') ? 'Native enabled' : 'Native disabled'}
                  color={Colors.Success}
                  active={getAdUnitMediatypes(adUnit).includes('Native') && !isDisabled}
                  enabled={adUnit.enabled}
                />
              </Qualifiers>
            )}
          </>
        )}
        {bidder && (
          <>
            <h3>Mapped with :</h3>
            {bidder.desktop && bidder.desktop.length > 0 && (
              <div>
                <strong>Desktop:</strong>
                {bidder.desktop.join(`\xa0\xa0|\xa0\xa0`)}
              </div>
            )}
            {bidder.mobile && bidder.mobile.length > 0 && (
              <div>
                <strong>Mobile:</strong>
                {bidder.mobile.join(`\xa0\xa0|\xa0\xa0`)}
              </div>
            )}
          </>
        )}
        {bidderRule && (
          <>
            <h3>Bidders:</h3>
            {bidderRule.bidders.length > 0 ? (
              bidderRule.bidders.map(({ name: bidderName, mediaTypes }) => (
                <div key={bidderName}>
                  <strong>{bidderName}</strong>
                  {mediaTypes.map((mediaType) => (
                    <MediaTypeBadge key={mediaType} mediaType={mediaType} variant="primary" />
                  ))}
                </div>
              ))
            ) : (
              <em>No bidder enabled</em>
            )}
            <Divider />
            <h3>Auto-add new bidders:</h3>
            {BIDDER_RULE_SECTIONS.map((section) => (
              <div key={section}>
                <strong>{BIDDER_RULE_SECTION_LABELS[section]}:</strong> {bidderRule.autoAddNewBidderSeats[section] ? 'Yes' : 'No'}
              </div>
            ))}
          </>
        )}
        {conditions && (
          <>
            <h3>Conditions:</h3> {conditions}
          </>
        )}
        {lazyLoadingRule && (
          <>
            <div>
              <h3>Lazy loading on HB:</h3> {lazyLoadingRule.headerBiddingEnabled ? 'Yes' : 'No'}
            </div>
            <div>
              <h3>Lazy loading on GAM:</h3> {lazyLoadingRule.gamEnabled ? 'Yes' : 'No'}
            </div>
            <div>
              <h3>Auction start:</h3> {lazyLoadingRule.headerBiddingEnabled ? `${lazyLoadingRule.prebidAuctionViewportsPercent} viewports` : 'not lazy loaded'}
            </div>
            <div>
              <h3>GAM request:</h3> {lazyLoadingRule.gamEnabled ? `${lazyLoadingRule.googletagFetchViewportsPercent} viewports` : 'not lazy loaded'}
            </div>
            <div>
              <h3>Render impression:</h3> {lazyLoadingRule.gamEnabled ? `${lazyLoadingRule.googletagRenderViewportsPercent} viewports` : 'not lazy loaded'}
            </div>
          </>
        )}

        {floorRule && (
          <FloorWrapper key={floorRule.ruleName}>
            <p>
              <strong>Default price</strong> - <em>{displayWithCurrency(Number.parseInt(floorRule.floor) / 100, currencySymbol)}</em>
            </p>
            {floorRule.settings?.map((setting, settingIndex) => (
              <FloorWrapper key={settingIndex}>
                <TargetValues>
                  <p>
                    <strong>Target {settingIndex + 1}</strong> - <em>{displayWithCurrency(setting.floor / 100, currencySymbol)}</em>
                  </p>
                  <div>
                    {setting.mediaTypes.map((mediaType) => (
                      <IconBackground key={mediaType}>
                        <Icon fill={Colors.White} name={mediaType === 'video' ? 'video' : mediaType === 'banner' ? 'banner' : 'native'} width={'20px'} />
                      </IconBackground>
                    ))}
                  </div>
                  {product !== 'pbs' && (
                    <>
                      {!setting.sizes.length ? (
                        <AllSizesChip text={'All inventory sizes selected'} color={Colors.Ghost}></AllSizesChip>
                      ) : (
                        setting.sizes.map((size, key) => <Chip key={key} text={size} color={Colors.Ghost}></Chip>)
                      )}
                    </>
                  )}
                </TargetValues>
              </FloorWrapper>
            ))}
          </FloorWrapper>
        )}

        {differences && (
          <>
            <Divider />
            <h2>
              <Icon width={'18px'} name={'sync_problem'} fill={Colors.Warning} />
              Out of sync
            </h2>
            {isDisabled ? (
              <Archived>
                <Icon width={'18px'} name={'warning'} fill={Colors.Alert} />
                <span>Archived</span>
              </Archived>
            ) : (
              differences.map((e) => <div key={e}>{e}</div>)
            )}
          </>
        )}
      </CenterItems>
    </RichDetailsWrapper>
  )
}

export const RichDetails = styled(BaseRichDetails)``
